import type { Locale, Role } from "@bodemleven/course-kit/schema/core";

interface HomeStep {
  title: string;
  body: string;
}

/** Public home, status pages, login intro, workspace frame and shared components. */
export interface ShellCopy {
  login: {
    signInIntro: string;
    registerIntro: string;
  };
  home: {
    title: string;
    eyebrow: string;
    heading: string;
    intro: string;
    signInCta: string;
    workspaceCta: string;
    signedInAs: (name: string) => string;
    stepsHeading: string;
    stepsIntro: string;
    /** Step number shown in the badge, e.g. "Stap 1". */
    stepLabel: (position: number) => string;
    steps: {
      land: HomeStep;
      sample: HomeStep;
      research: HomeStep;
      advice: HomeStep;
    };
    lookHeading: string;
    lookIntro: string;
    look: { title: string; body: string }[];
    labHeading: string;
    labBody: string;
  };
  notFound: {
    title: string;
    body: string;
    back: string;
  };
  /** Signed in, but the page needs at least this role. */
  noAccess: {
    analyst: { title: string; body: string };
    admin: { title: string; body: string };
    back: string;
  };
  workspace: {
    signedInAs: string;
    analystHint: string;
  };
  /** What each role may do on this site, for the users page. */
  roles: Record<Role, string>;
  /** `/onderzoek`: next actions for owners, queues for analysts. */
  overview: {
    descriptionOwner: string;
    descriptionAnalyst: string;
    /** Link to the full list below a section, e.g. "Alle monsters". */
    showAll: (section: string) => string;
    toSend: { heading: string; description: string; empty: string; emptyAction: string };
    results: { heading: string; description: string; empty: string; emptyAction: string };
    advice: { heading: string; description: string; empty: string; emptyAction: string };
    toReceive: { empty: string };
    inProgress: { heading: string; description: string; empty: string; emptyAction: string };
    sampledOn: (date: string) => string;
    publishedOn: (date: string) => string;
  };
  boundary: {
    notFoundTitle: string;
    errorTitle: string;
    backToWorkspace: string;
  };
  score: {
    none: string;
    /** Visible label, e.g. "72/100 · Redelijk"; without a grade (partial coverage) just "72/100". */
    value: (score: string, grade: string | null) => string;
  };
}

export const shellCopy: Record<Locale, ShellCopy> = {
  nl: {
    login: {
      signInIntro:
        "Log in om je gebieden, monsters en onderzoeksresultaten te bekijken.",
      registerIntro:
        "Maak een account aan om je gebieden vast te leggen en monsters " +
        "naar het lab te sturen.",
    },
    home: {
      title: "Bodemonderzoek",
      eyebrow: "Bodemlevenlab van Wilma Plooij",
      heading: "Zie wat er écht leeft in je bodem",
      intro:
        "Onder de microscoop tellen we bacteriën, schimmels, protozoën en aaltjes in jouw " +
        "grond. Je krijgt een heldere bodemlevenscore, grafieken en een advies dat " +
        "past bij jouw perceel.",
      signInCta: "Inloggen of account maken",
      workspaceCta: "Naar mijn bodemonderzoek",
      signedInAs: (name) => `Je bent ingelogd als ${name}.`,
      stepsHeading: "Zo werkt het",
      stepsIntro: "Van perceel tot advies in vier stappen.",
      stepLabel: (position) => `Stap ${position}`,
      steps: {
        land: {
          title: "Leg je gebied vast",
          body:
            "Geef je perceel, moestuin of weide een naam en vertel wat er groeit, welke " +
            "grondsoort het is en hoe je het beheert.",
        },
        sample: {
          title: "Neem een monster",
          body:
            "Steek een paar happen grond, vul de gegevens in en stuur het monster met de " +
            "code op naar het lab.",
        },
        research: {
          title: "Wij onderzoeken het bodemleven",
          body:
            "Wilma bekijkt je monster onder de microscoop en meet schimmels, bacteriën, " +
            "protozoën en aaltjes.",
        },
        advice: {
          title: "Je krijgt een advies",
          body:
            "Je ziet de uitslag met grafieken en krijgt concrete maatregelen om het " +
            "bodemleven te versterken.",
        },
      },
      lookHeading: "Waar we naar kijken",
      lookIntro: "Het bodemvoedselweb: wie leeft er, hoeveel en in welke verhouding?",
      look: [
        {
          title: "Schimmels en bacteriën",
          body:
            "De basis van het voedselweb. Hun verhouding vertelt hoe ver je bodem " +
            "ontwikkeld is.",
        },
        {
          title: "Protozoën",
          body:
            "Flagellaten, amoeben en trilhaardiertjes eten bacteriën en maken voeding vrij " +
            "voor de plant.",
        },
        {
          title: "Aaltjes",
          body:
            "Nuttige aaltjes houden het systeem in balans; worteleters wijzen op een " +
            "verstoring.",
        },
      ],
      labHeading: "Persoonlijk en met de hand onderzocht",
      labBody:
        "Elk monster wordt door een analist bekeken, niet door een machine. Je ziet alleen " +
        "je eigen gegevens; uitslagen en adviezen verschijnen zodra ze klaar zijn.",
    },
    notFound: {
      title: "Pagina niet gevonden",
      body: "Deze pagina bestaat niet (meer). Controleer het adres of ga terug naar het begin.",
      back: "Naar de beginpagina",
    },
    noAccess: {
      analyst: {
        title: "Alleen voor de analist",
        body:
          "Dit deel is bedoeld voor het lab. Je eigen gebieden, monsters en uitslagen kun je " +
          "gewoon bekijken.",
      },
      admin: {
        title: "Alleen voor beheerders",
        body:
          "Dit deel is voor de beheerders van het lab: kalibraties, runnersleutels en de " +
          "instellingen van de AI.",
      },
      back: "Naar mijn bodemonderzoek",
    },
    workspace: {
      signedInAs: "Ingelogd als",
      analystHint: "Je ziet alle gebieden en doet het labwerk.",
    },
    roles: {
      learner:
        "Ziet alleen de eigen gebieden, monsters, afgeronde onderzoeken en gepubliceerde adviezen.",
      analyst:
        "Ziet alle gebieden en doet het labwerk: onderzoeken, foto's, AI-voorstellen controleren " +
        "en adviezen.",
      admin:
        "Alles wat een analist kan, plus de Lab-instellingen (kalibraties, runnersleutels, " +
        "AI-stappen) en analisten aanwijzen.",
      owner: "Alles wat een beheerder kan, en beheerders aanwijzen.",
    },
    overview: {
      descriptionOwner: "Wat er voor je klaarstaat en wat je volgende stap is.",
      descriptionAnalyst: "Het werk dat in het lab op je wacht.",
      showAll: (section) => `Alle ${section.toLowerCase()}`,
      toSend: {
        heading: "Monsters om te versturen",
        description: "Monsters die je hebt vastgelegd maar nog niet naar het lab hebt gestuurd.",
        empty:
          "Er wacht geen monster op verzending. Neem een nieuw monster om je bodem te laten " +
          "onderzoeken.",
        emptyAction: "Nieuw monster",
      },
      results: {
        heading: "Nieuwe uitslagen",
        description: "Afgeronde onderzoeken, nieuwste eerst.",
        empty:
          "Nog geen uitslagen. Zodra het lab je monster heeft onderzocht, staat de uitslag hier.",
        emptyAction: "Naar mijn monsters",
      },
      advice: {
        heading: "Adviezen",
        description: "Maatregelen van de analist op basis van je uitslagen.",
        empty: "Nog geen advies. Na een afgeronde uitslag schrijft de analist je advies.",
        emptyAction: "Naar mijn onderzoeken",
      },
      toReceive: {
        empty: "Er is geen verstuurd monster dat nog ontvangen moet worden.",
      },
      inProgress: {
        heading: "Onderzoek in uitvoering",
        description: "Concept-onderzoeken die je nog moet afronden.",
        empty: "Geen onderzoek in uitvoering. Begin een onderzoek bij een ontvangen monster.",
        emptyAction: "Naar de monsters",
      },
      sampledOn: (date) => `Genomen op ${date}`,
      publishedOn: (date) => `Gepubliceerd op ${date}`,
    },
    boundary: {
      notFoundTitle: "Niet gevonden",
      errorTitle: "Er ging iets mis",
      backToWorkspace: "Naar het overzicht",
    },
    score: {
      none: "Nog geen score",
      value: (score, grade) => (grade ? `${score}/100 · ${grade}` : `${score}/100`),
    },
  },
  en: {
    login: {
      signInIntro: "Sign in to see your areas, samples and research results.",
      registerIntro:
        "Create an account to record your areas and send samples to the lab.",
    },
    home: {
      title: "Soil research",
      eyebrow: "Wilma Plooij's soil-life lab",
      heading: "See what really lives in your soil",
      intro:
        "Under the microscope we count the bacteria, fungi, protozoa and nematodes in your " +
        "soil. You get a clear soil-life score, charts and advice that fits " +
        "your land.",
      signInCta: "Sign in or create an account",
      workspaceCta: "Go to my soil research",
      signedInAs: (name) => `You are signed in as ${name}.`,
      stepsHeading: "How it works",
      stepsIntro: "From field to advice in four steps.",
      stepLabel: (position) => `Step ${position}`,
      steps: {
        land: {
          title: "Record your area",
          body:
            "Name your field, vegetable garden or pasture and tell us what grows there, the " +
            "soil type and how you manage it.",
        },
        sample: {
          title: "Take a sample",
          body:
            "Take a few cores of soil, fill in the details and post the sample with its " +
            "code to the lab.",
        },
        research: {
          title: "We study the soil life",
          body:
            "Wilma examines your sample under the microscope and measures fungi, bacteria, " +
            "protozoa and nematodes.",
        },
        advice: {
          title: "You receive advice",
          body:
            "You see the results with charts and get concrete measures to strengthen your " +
            "soil life.",
        },
      },
      lookHeading: "What we look at",
      lookIntro: "The soil food web: who lives there, how many and in what balance?",
      look: [
        {
          title: "Fungi and bacteria",
          body: "The base of the food web. Their ratio shows how far your soil has developed.",
        },
        {
          title: "Protozoa",
          body:
            "Flagellates, amoebae and ciliates eat bacteria and release nutrients for the " +
            "plant.",
        },
        {
          title: "Nematodes",
          body:
            "Beneficial nematodes keep the system in balance; root feeders point to a " +
            "disturbance.",
        },
      ],
      labHeading: "Personal, examined by hand",
      labBody:
        "Every sample is examined by an analyst, not a machine. You only see your own data; " +
        "results and advice appear as soon as they are ready.",
    },
    notFound: {
      title: "Page not found",
      body: "This page does not exist (any more). Check the address or go back to the start.",
      back: "Go to the home page",
    },
    noAccess: {
      analyst: {
        title: "For the analyst only",
        body:
          "This part is meant for the lab. You can still view your own areas, samples " +
          "and results.",
      },
      admin: {
        title: "For admins only",
        body: "This part is for the lab's admins: calibrations, runner keys and the AI settings.",
      },
      back: "Go to my soil research",
    },
    workspace: {
      signedInAs: "Signed in as",
      analystHint: "You see every area and do the lab work.",
    },
    roles: {
      learner: "Sees only their own areas, samples, completed research and published advice.",
      analyst:
        "Sees every area and does the lab work: research, photos, reviewing AI suggestions " +
        "and advice.",
      admin:
        "Everything an analyst can do, plus the Lab settings (calibrations, runner keys, " +
        "AI steps) and appointing analysts.",
      owner: "Everything an admin can do, and appointing admins.",
    },
    overview: {
      descriptionOwner: "What is ready for you and what your next step is.",
      descriptionAnalyst: "The work waiting for you in the lab.",
      showAll: (section) => `All ${section.toLowerCase()}`,
      toSend: {
        heading: "Samples to send",
        description: "Samples you recorded but have not sent to the lab yet.",
        empty: "No sample is waiting to be sent. Take a new sample to have your soil analysed.",
        emptyAction: "New sample",
      },
      results: {
        heading: "New results",
        description: "Completed analyses, newest first.",
        empty: "No results yet. Once the lab has analysed your sample, the result appears here.",
        emptyAction: "Go to my samples",
      },
      advice: {
        heading: "Advice",
        description: "Measures from the analyst based on your results.",
        empty: "No advice yet. After a completed result the analyst writes your advice.",
        emptyAction: "Go to my analyses",
      },
      toReceive: {
        empty: "There is no sent sample waiting to be received.",
      },
      inProgress: {
        heading: "Analyses in progress",
        description: "Draft analyses you still need to complete.",
        empty: "No analysis in progress. Start one on a received sample.",
        emptyAction: "Go to the samples",
      },
      sampledOn: (date) => `Taken on ${date}`,
      publishedOn: (date) => `Published on ${date}`,
    },
    boundary: {
      notFoundTitle: "Not found",
      errorTitle: "Something went wrong",
      backToWorkspace: "Back to the overview",
    },
    score: {
      none: "No score yet",
      value: (score, grade) => (grade ? `${score}/100 · ${grade}` : `${score}/100`),
    },
  },
};
